import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {getCoverGroups} from '@amblique/king-living/app/utils/customize-selections';
import {sumSelectionPrices, sumSelectionListPrices} from '@amblique/king-living/app/components/complex-customize/utils/selection-utils';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {CustomizeStepsContent} from '@amblique/king-living/app/components/complex-customize/customize-steps-content';

/**
 * Flyout shell for the customize journey.
 *
 * - Step locking is ENABLED: each step is locked until the previous one is complete.
 * - SubtotalFooter is shown.
 * - "Done" commits the draft to committedSelection.
 * - Discard runs when the enclosing drawer/panel closes with cancel (ConfigurationPanel handleClose → cancelFlyout), not via a prop on this shell.
 *
 * Reads selections from flyoutDraftState.draftSelections.
 * Passes updateDraftSelection as the onSelect handler so all selections flow through the context.
 *
 * The master product always drives step structure (cover groups, enriched OGU, recommendations).
 * Linked set products do not carry variant data or enriched OGU, so they cannot drive rendering.
 * Pre-selected values from a set are captured in draftSelections by editSet / selectSet.
 * The editingProductId tracks which set is being edited (for deviation detection, dropdown state).
 *
 * @param {Object} props
 * @param {Object} props.product - Master product (masterA); always used for step rendering
 * @param {number} [props.drawerHeight] - Drawer height for scroll body sizing
 * @param {Function} [props.onGalleryProductChange] - Called when gallery product changes
 * @param {Function} [props.onHeaderChange] - Called when panel header info changes
 * @param {Function} [props.onSlotProductPreview] - Called when a slot product is previewed
 * @param {Function} [props.onSubViewChange] - Called when entering/leaving a sub-view
 * @param {Function} [props.onViewDetails] - Called when "View details" is clicked
 */
export function CustomizeStepsFlyout({
  product,
  drawerHeight,
  onGalleryProductChange,
  onHeaderChange,
  onSlotProductPreview,
  onSubViewChange,
  onViewDetails,
}) {
  const {
    committedSelection,
    flyoutDraftState,
    setCards,
    setCardTotals,
    setCardListTotals,
    setProductsMap,
    updateDraftSelection,
    markFlyoutUserInteracted,
    commitFlyout,
  } = useCustomizeJourney();

  const coverGroups = useMemo(() => getCoverGroups(product), [product]);

  /**
   * Master product min price — used as subtotal fallback before any selections have prices.
   * Parsed from c_priceProcessed which is always available on the master product.
   */
  const masterMinPrice = useMemo(() => {
    try {
      const raw = product?.c_priceProcessed;
      const parsed = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};

      return parsed?.min ?? parsed?.salePrice ?? null;
    } catch {
      return null;
    }
  }, [product]);

  /**
   * Subtotal price for the footer.
   * - Set mode, not yet deviated: use the recalculated set card total (setCardTotals) which
   *   matches what the set-card-item block displays. Falls back to card.price from SFCC if
   *   setCardTotals hasn't resolved yet, then to masterMinPrice.
   * - Customize mode or deviated from set: sum prices from draft selections.
   * - Falls back to master product min price when no selection prices are available yet.
   */
  const subtotalPrice = useMemo(() => {
    if (committedSelection.mode === 'set' && !flyoutDraftState.isDeviatingFromSet) {
      const setId = committedSelection.productId;
      const calculatedTotal = setCardTotals[setId];

      if (calculatedTotal != null) return calculatedTotal;

      const card = setCards.find((c) => c.productId === setId);

      return card?.price ?? masterMinPrice;
    }

    return sumSelectionPrices(flyoutDraftState.draftSelections) ?? masterMinPrice;
  }, [committedSelection, flyoutDraftState, masterMinPrice, setCardTotals, setCards]);

  const subtotalListPrice = useMemo(() => {
    if (committedSelection.mode === 'set' && !flyoutDraftState.isDeviatingFromSet) {
      const setId = committedSelection.productId;

      return setCardListTotals?.[setId] ?? 0;
    }

    return sumSelectionListPrices(flyoutDraftState.draftSelections);
  }, [committedSelection, flyoutDraftState, setCardListTotals]);

  const isPredefinedSetView = Boolean(
    flyoutDraftState.editingProductId &&
    setProductsMap[flyoutDraftState.editingProductId] &&
    !flyoutDraftState.isDeviatingFromSet
  );

  return (
    <CustomizeStepsContent
      key={flyoutDraftState.editingProductId ?? 'customize'}
      coverGroups={coverGroups}
      drawerHeight={drawerHeight}
      hasUserInteracted={flyoutDraftState.hasUserInteracted}
      isPredefinedSetView={isPredefinedSetView}
      product={product}
      selections={flyoutDraftState.draftSelections}
      subtotalListPrice={subtotalListPrice}
      subtotalPrice={subtotalPrice}
      enableLocking
      onDone={commitFlyout}
      onGalleryProductChange={onGalleryProductChange}
      onHeaderChange={onHeaderChange}
      onSelect={updateDraftSelection}
      onSlotProductPreview={onSlotProductPreview}
      onSubViewChange={onSubViewChange}
      onUserInteract={markFlyoutUserInteracted}
      onViewDetails={onViewDetails}
    />
  );
}

CustomizeStepsFlyout.displayName = 'CustomizeStepsFlyout';

CustomizeStepsFlyout.propTypes = {
  product: PropTypes.object.isRequired,
  drawerHeight: PropTypes.number,
  onGalleryProductChange: PropTypes.func,
  onHeaderChange: PropTypes.func,
  onSlotProductPreview: PropTypes.func,
  onSubViewChange: PropTypes.func,
  onViewDetails: PropTypes.func,
};
